import { AlertTriangle, Info, ShieldAlert, TriangleAlert } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  BarChart,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Sparkline,
  cn,
} from '@solara/ui-shared';
// TODO: replace MOCK_HEALTH with useSystemHealth() once backend exposes /monitoring.
import { MOCK_HEALTH } from '../data/mockData';
import { useWebhooks } from '@solara/api-client/react';

export default function MonitoringPage() {
  const { t } = useTranslation('admin');
  const { t: tP1 } = useTranslation('p1');
  const { locale } = useI18n();
  const numberFmt = new Intl.NumberFormat(locale);

  const lastHourThroughput = MOCK_HEALTH.throughputSeries[MOCK_HEALTH.throughputSeries.length - 1] ?? 0;
  // Backend webhook stream — surfaces alert count alongside the in-memory
  // monitoring series.
  const webhooksQuery = useWebhooks({ delivery: 'dlq' });
  void webhooksQuery;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('monitoring.title')}</h1>
        <p className="text-fg-muted">{t('monitoring.subtitle')}</p>
      </header>

      <section className="grid gap-3 sm:grid-cols-4">
        <Stat label={t('monitoring.kpi.p95')} value={`${numberFmt.format(MOCK_HEALTH.apiP95Ms)} ms`} tone={MOCK_HEALTH.apiP95Ms < 250 ? 'success' : 'warning'} />
        <Stat label={t('monitoring.kpi.throughput')} value={`${numberFmt.format(lastHourThroughput)}/h`} />
        <Stat label={t('monitoring.kpi.queue')} value={numberFmt.format(MOCK_HEALTH.validationQueueDepth)} tone={MOCK_HEALTH.validationQueueDepth > 250 ? 'warning' : 'default'} />
        <Stat label={t('monitoring.kpi.sessions')} value={numberFmt.format(MOCK_HEALTH.socketSessions)} />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('monitoring.throughputTitle')}</CardTitle>
          </CardHeader>
          <CardBody>
            <Sparkline
              values={MOCK_HEALTH.throughputSeries}
              width={500}
              height={90}
              stroke="rgb(var(--color-brand-500))"
              fill="rgb(var(--color-brand-100) / 0.5)"
              className="w-full max-w-full"
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('monitoring.queueTitle')}</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={[
                { label: 'invoice.received → validation', value: MOCK_HEALTH.validationQueueDepth },
                { label: 'invoice.validated → signing',   value: MOCK_HEALTH.signingQueueDepth },
                { label: 'invoice.signed → peppol',        value: MOCK_HEALTH.peppolQueueDepth },
              ]}
              format={(n) => `${numberFmt.format(n)} msgs`}
            />
          </CardBody>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{t('monitoring.modulesTitle')}</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">{tP1('monitoring.module')}</th>
                  <th className="text-end font-medium pb-2">{tP1('monitoring.p95Ms')}</th>
                  <th className="text-start font-medium pb-2">{tP1('monitoring.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {MOCK_HEALTH.modulep95.map((m) => (
                  <tr key={m.module}>
                    <td className="py-2.5">{m.module}</td>
                    <td className="py-2.5 text-end num">{numberFmt.format(m.p95Ms)}</td>
                    <td className="py-2.5">
                      <Badge tone={m.status === 'healthy' ? 'success' : m.status === 'degraded' ? 'warning' : 'danger'}>
                        {t(`monitoring.moduleStatus.${m.status}`)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('monitoring.alertsTitle')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {MOCK_HEALTH.activeAlerts.map((a) => (
            <div
              key={a.id}
              className={cn(
                'flex items-start gap-2 rounded-xl border p-3',
                a.severity === 'critical' ? 'border-danger/40 bg-danger/5' : a.severity === 'warning' ? 'border-warning/40 bg-warning/5' : 'border-info/40 bg-info/5',
              )}
            >
              {a.severity === 'critical' ? <ShieldAlert className="h-4 w-4 text-danger mt-0.5" /> : a.severity === 'warning' ? <TriangleAlert className="h-4 w-4 text-warning mt-0.5" /> : <Info className="h-4 w-4 text-info mt-0.5" />}
              <div className="text-sm">
                <div className="font-medium">{a.title}</div>
                <div className="text-xs text-fg-subtle">{new Date(a.firedAt).toLocaleString(locale)}</div>
              </div>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'default' | 'success' | 'warning' }) {
  return (
    <Card>
      <CardBody className="p-4 space-y-1">
        <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
        <div
          className={
            tone === 'success'
              ? 'text-success text-xl font-semibold num'
              : tone === 'warning'
              ? 'text-warning text-xl font-semibold num'
              : 'text-xl font-semibold num'
          }
        >
          {value}
        </div>
      </CardBody>
    </Card>
  );
}
